import { Container, Grid2, Stack } from "@mui/material";
import { UnfinishedGamesViewModel } from "./UnfinishedGamesViewModel";
import { useTranslation } from "react-i18next";
import { HeaderView } from "../../component/HeaderView";
import { LoadingView } from "../../component/LoadingView";
import { GameData } from "../../../domain/model/Game";
import { CustomCardView } from "../../component/CustomCardView";
import { CustomTextView } from "../../component/CustomTextView";
import { COLOR_LIGHT_GRAY } from "../../../data/colors/Colors";
import { CategoryItemView } from "../playedGames/PlayedGamesView";
import { CustomButtonView } from "../../component/CustomButtonView";
import { useNavigate } from "react-router-dom";
import { providePlayerRemoveFromUnfinishedGamesUseCase } from "../../di/UseCasesModule";
import { EmptyView } from "../../component/EmptyView";
import { Player } from "../../../domain/model/User";

export function UnfnishedGamesView({ player }: { player: Player | undefined }) {
  const navigate = useNavigate();
  const viewModel = UnfinishedGamesViewModel(
    providePlayerRemoveFromUnfinishedGamesUseCase()
  );

  const { t } = useTranslation();

  const unfinishedGames = player?.unfinishedGames;

  return (
    <Stack>
      <HeaderView
        title={t("unfinished_games")}
        isLayoutFullWidthInSmallerScreens={false}
      />

      {!unfinishedGames ? (
        <LoadingView />
      ) : (
        <>
          {unfinishedGames.length == 0 ? (
            <EmptyView />
          ) : (
            <Container maxWidth={false}>
              <Grid2 container spacing={2} pb={3} pt={3}>
                {unfinishedGames?.map((unfinishedGame, index) => {
                  const gameData =
                    viewModel.convertUnfinshedGameToGameData(unfinishedGame);
                  return (
                    <Grid2
                      size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
                      key={index}
                    >
                      <UnfinishedGameItemView
                        gameData={gameData}
                        index={index}
                        onContinueClicked={() => {
                          viewModel.continueGame(index, gameData);
                        }}
                        onDeleteClicked={async () => {
                          await viewModel.removeFromUnfinishedGames(index);
                          navigate(0);
                        }}
                      />
                    </Grid2>
                  );
                })}
              </Grid2>
            </Container>
          )}
        </>
      )}
    </Stack>
  );
}

function UnfinishedGameItemView({
  gameData,
  index,
  onDeleteClicked,
  onContinueClicked,
}: {
  gameData: GameData;
  index: number;
  onDeleteClicked: () => void;
  onContinueClicked: () => void;
}) {
  const { t } = useTranslation();
  return (
    <CustomCardView
      children={
        <Stack width={"100%"} gap={2}>
          <Stack
            gap={1}
            direction={"row"}
            alignItems={"center"}
            width={"100%"}
            justifyContent={"space-between"}
          >
            <CustomTextView
              text={`#${index + 1}`}
              variant="h5"
              fontWeight="bold"
            />
            <Stack alignItems={"center"} direction={"row"} gap={1}>
              <CustomButtonView
                text={t("delete_game")}
                backgroundColor="red"
                onClick={onDeleteClicked}
              />
              <CustomButtonView
                text={t("continue_game")}
                backgroundColor="black"
                onClick={onContinueClicked}
              />
            </Stack>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            width={"100%"}
            justifyContent={"space-between"}
            gap={1}
          >
            <Stack>
              <CustomTextView
                text={t("first_team")}
                variant="body2"
                textColor={COLOR_LIGHT_GRAY}
              />
              <CustomTextView text={gameData.firstTeamName} variant="h6" />
            </Stack>
            <Stack gap={1} direction={"row"} alignItems={"center"}>
              <CustomTextView
                text={gameData.firstTeamScore.toString()}
                variant="h5"
                fontWeight="bold"
              />
              <CustomTextView text={"-"} variant="h3" />
              <CustomTextView
                text={gameData.secondTeamScore.toString()}
                variant="h5"
                fontWeight="bold"
              />
            </Stack>
            <Stack alignItems={"end"}>
              <CustomTextView
                text={t("second_team")}
                variant="body2"
                textColor={COLOR_LIGHT_GRAY}
              />
              <CustomTextView text={gameData.secondTeamName} variant="h6" />
            </Stack>
          </Stack>
          <Grid2 container spacing={1}>
            {gameData.categories.map((c) => {
              return (
                <Grid2 key={c.id} size={4}>
                  <CategoryItemView category={c} />
                </Grid2>
              );
            })}
          </Grid2>
        </Stack>
      }
    />
  );
}
